import { useEffect } from 'react';

import {
  applyPredicates,
  armedPredicates,
  OPS_FOR,
  type FilterPredicate,
} from '../model/filter.js';
import type { MwModelWire, MwTrackWire } from '../model/mw.js';
import {
  categoryOptionsFor,
  cellFor,
  columnByKey,
  FILTERABLE_COLUMNS,
  RECOGNITION_COLUMNS,
  observedSignature,
  predicateText,
  valueOf,
  type RecognitionColumn,
} from '../model/recognition.js';
import { useConsoleStore } from '../state/store.js';
import { DATA, DATA_LABEL, DATA_TAG, HEADLINE } from '../../theme/type.js';
import { CHIP } from './chrome.js';

/**
 * RB §5.3.5's recognition table, as an overlay (design 996–1135).
 *
 * A reference document the operator opens, reads and closes. It does **not** know
 * which track is selected and never filters itself against one: the player draws
 * the conclusion, and a table that highlighted the matching row would be the
 * console making the call the seat is being graded on.
 *
 * The one concession is the pinned observed-signature strip, which puts this
 * track's readings on the table's own columns — and its cells *seed* a filter
 * rather than applying one, so the operator still writes the predicate.
 *
 * ## It covers the centre pane, not the console
 *
 * Same rule as the alert lane (Phase 1's finding 8) and the toast (Phase 3's
 * finding 15). It also happens to be the better interaction: the rails stay up,
 * so the track's own detail panel is beside the table rather than underneath it.
 *
 * The cost is width, and the cost is real: at 1280 the centre pane is 640 px
 * against a table that wants 1136. It is paid by **scrolling**, not by dropping
 * columns. The table used to take whole priority tiers and withhold the rest,
 * which meant that at the committed floor it showed what stages 1 and 2
 * discriminate on and silently withheld stages 3 and 4 — the operator's newest
 * readings, and the pinned strip's with them, since the strip is computed
 * against the same list. TYPE is frozen at the left so the row label is never
 * what scrolls away.
 */

export interface RecognitionTableProps {
  readonly catalogue: readonly MwModelWire[];
  /** The selected track, for the pinned strip. Null draws the table with no strip. */
  readonly track: MwTrackWire | null;
  readonly onClose: () => void;
}

export function RecognitionTable(props: RecognitionTableProps): JSX.Element {
  const filters = useConsoleStore((state) => state.mwFilters);
  const filtersOpen = useConsoleStore((state) => state.mwFiltersOpen);
  const setFiltersOpen = useConsoleStore((state) => state.setMwFiltersOpen);
  const addFilter = useConsoleStore((state) => state.addMwFilter);
  const setFilter = useConsoleStore((state) => state.setMwFilter);
  const removeFilter = useConsoleStore((state) => state.removeMwFilter);
  const clearFilters = useConsoleStore((state) => state.clearMwFilters);
  const rowOpen = useConsoleStore((state) => state.mwRowOpen);
  const setRowOpen = useConsoleStore((state) => state.setMwRowOpen);

  const { onClose } = props;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Every column, always. The table scrolls sideways to reach the later ones and
  // freezes TYPE at the left; it does not decide on the operator's behalf which
  // stages they may compare against. See the note in `model/recognition.ts`.
  const columns = RECOGNITION_COLUMNS;
  const rows = applyPredicates(props.catalogue, filters, valueOf);
  const armed = armedPredicates(filters);
  const observed = props.track === null ? null : observedSignature(props.track, columns);

  return (
    <div
      data-testid="mw-recognition"
      onClick={props.onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 'var(--z-overlay)',
        display: 'flex',
        padding: 16,
        background: 'var(--scrim)',
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            height: 40,
            padding: '0 14px',
            background: 'var(--surface-2)',
            borderBottom: '1px solid var(--border-strong)',
          }}
        >
          <span style={{ ...HEADLINE, flex: 1, fontSize: 13, color: 'var(--text)' }}>
            RECOGNITION TABLE
          </span>
          <span
            data-testid="mw-table-count"
            style={{ ...DATA, fontSize: 12, color: 'var(--text-2)' }}
          >
            {rows.length}
          </span>
          <button
            type="button"
            data-testid="mw-table-close"
            // The visible label is the key that also does it, which is the right
            // thing to show and the wrong thing to announce.
            aria-label="Close the recognition table"
            onClick={props.onClose}
            style={CHIP}
          >
            ESC
          </button>
        </div>

        <div
          style={{
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '7px 14px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              data-testid="mw-filters-toggle"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen(!filtersOpen)}
              style={CHIP}
            >
              FILTERS
            </button>
            {armed.length > 0 && (
              <span data-testid="mw-filter-count" style={{ ...DATA, color: 'var(--primary)' }}>
                {armed.length}
              </span>
            )}
            <span style={{ flex: 1 }} />
            {filters.length > 0 && (
              <button
                type="button"
                data-testid="mw-filters-clear"
                onClick={clearFilters}
                style={CHIP}
              >
                CLEAR ALL
              </button>
            )}
            <button
              type="button"
              data-testid="mw-filter-add"
              onClick={() => addFilter()}
              style={CHIP}
            >
              + FILTER
            </button>
          </div>

          {filtersOpen &&
            filters.map((filter) => (
              <PredicateRow
                key={filter.id}
                filter={filter}
                onChange={(patch) => setFilter(filter.id, patch)}
                onRemove={() => removeFilter(filter.id)}
              />
            ))}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ minWidth: 'min-content' }}>
            <div
              data-testid="mw-table-head"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 'var(--z-panel-sticky)',
                display: 'flex',
                alignItems: 'center',
                height: 26,
                background: 'var(--surface-2)',
                borderBottom: '1px solid var(--border-strong)',
              }}
            >
              {columns.map((column) => (
                <span
                  key={column.key}
                  data-testid="mw-table-col"
                  data-column={column.key}
                  style={{
                    ...CELL,
                    width: column.width,
                    ...HEAD,
                    ...(column.key === 'type' ? { ...FROZEN, background: 'var(--surface-2)' } : {}),
                  }}
                >
                  {column.label}
                </span>
              ))}
            </div>

            {observed !== null && props.track !== null && (
              <div
                data-testid="mw-observed-strip"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 30,
                  background: 'var(--surface-3)',
                  borderBottom: '1px solid var(--border-strong)',
                }}
              >
                <span
                  style={{
                    ...CELL,
                    width: columns[0]?.width ?? 180,
                    fontWeight: 700,
                    color: 'var(--primary)',
                    ...FROZEN,
                    background: 'var(--surface-3)',
                  }}
                >
                  OBSERVED · {props.track.eventId}
                </span>
                {observed.map((cell) => {
                  const column = columnByKey(cell.key);
                  const width = column?.width ?? 100;
                  if (cell.state === 'OBSERVED' && cell.seed !== null) {
                    return (
                      <button
                        key={cell.key}
                        type="button"
                        data-testid="mw-observed-cell"
                        data-column={cell.key}
                        data-obs-state={cell.state}
                        onClick={() => addFilter(cell.key)}
                        style={{
                          ...CELL,
                          width,
                          background: 'transparent',
                          border: 0,
                          borderBottom: '1px dotted var(--primary)',
                          textAlign: 'left',
                          cursor: 'pointer',
                          color: 'var(--text)',
                        }}
                      >
                        {cell.text}
                      </button>
                    );
                  }
                  return (
                    <span
                      key={cell.key}
                      data-testid="mw-observed-cell"
                      data-column={cell.key}
                      data-obs-state={cell.state}
                      style={{
                        ...CELL,
                        width,
                        color: cell.state === 'DROPPED' ? 'var(--warn)' : 'var(--text-muted)',
                      }}
                    >
                      {/* Three absences, three readings. `—` is a point the
                          constellation dropped; a stage number is a reading that
                          has not arrived yet; blank is a column with nothing to
                          observe at all. */}
                      {cell.state === 'OBSERVED'
                        ? cell.text
                        : cell.state === 'DROPPED'
                          ? '—'
                          : cell.state === 'PENDING'
                            ? 'DUE S' + String(cell.dueAtStage ?? '')
                            : ''}
                    </span>
                  );
                })}
              </div>
            )}

            {rows.map((model) => (
              <div key={model.id} data-testid="mw-table-row" data-model={model.id}>
                <button
                  type="button"
                  onClick={() => setRowOpen(rowOpen === model.id ? null : model.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    minHeight: 34,
                    background: 'transparent',
                    border: 0,
                    borderBottom: '1px solid var(--rule-faint)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {columns.map((column) => {
                    const cell = cellFor(model, column);
                    return (
                      <span
                        key={column.key}
                        data-testid="mw-table-cell"
                        data-column={column.key}
                        style={{
                          ...CELL,
                          width: column.width,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          gap: 1,
                          color: column.key === 'type' ? 'var(--text)' : 'var(--text-2)',
                          ...(column.key === 'type'
                            ? { ...FROZEN, background: 'var(--surface)' }
                            : {}),
                        }}
                      >
                        <span
                          style={
                            // The type name wraps rather than elides. Eight of
                            // eleven catalogue names were cut off in the one
                            // column whose job is naming the type, and a row
                            // that gains a line is cheaper than a name the
                            // operator cannot read. Every other column keeps its
                            // single line and gives its full text to the
                            // expanded note.
                            column.key === 'type'
                              ? { whiteSpace: 'normal', lineHeight: 1.25 }
                              : {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                          }
                        >
                          {cell.text}
                        </span>
                        {cell.prose !== null && (
                          <span
                            data-testid="mw-table-prose"
                            style={{
                              // Was 9.5px, below the system's own 10px floor, on
                              // the rulebook sentence under a band.
                              ...DATA_TAG,
                              textTransform: 'none',
                              letterSpacing: '.04em',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: 'var(--text-muted)',
                            }}
                          >
                            {cell.prose}
                          </span>
                        )}
                      </span>
                    );
                  })}
                </button>
                {rowOpen === model.id && (
                  <div
                    data-testid="mw-table-note"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      padding: '8px 14px 10px',
                      background: 'var(--surface-2)',
                      borderBottom: '1px solid var(--rule)',
                    }}
                  >
                    {/* Every column, including the ones this width dropped. The
                        expanded row is where a narrow console gives them back. */}
                    {RECOGNITION_EXTRA.map((key) => {
                      const column = columnByKey(key);
                      if (column === undefined) return null;
                      const cell = cellFor(model, column);
                      return (
                        <span
                          key={key}
                          data-testid="mw-note-row"
                          data-column={key}
                          style={{
                            ...DATA,
                            display: 'flex',
                            gap: 10,
                            fontWeight: 400,
                            color: 'var(--text-2)',
                          }}
                        >
                          <span style={{ flex: 'none', width: 110, color: 'var(--text-muted)' }}>
                            {column.label}
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>{cell.text}</span>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {rows.length === 0 && (
              <div
                data-testid="mw-table-empty"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '28px 14px',
                }}
              >
                <span style={{ ...HEADLINE, fontSize: 13, color: 'var(--text-2)' }}>
                  NO TYPES MATCH
                </span>
                {/* The way out is the filters that did it, each one removable. */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {armed.map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      data-testid="mw-empty-filter"
                      onClick={() => removeFilter(filter.id)}
                      style={CHIP}
                    >
                      {predicateText(filter)} ✕
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The columns the expanded note always lists, whatever the width dropped. */
const RECOGNITION_EXTRA = [
  'category',
  'irIntensity',
  'boostDurationS',
  'stagingEvents',
  'apogeeKm',
  'groundTrack',
  'terminal',
  'rv',
] as const;

function PredicateRow(props: {
  readonly filter: FilterPredicate;
  readonly onChange: (patch: Partial<Omit<FilterPredicate, 'id'>>) => void;
  readonly onRemove: () => void;
}): JSX.Element {
  const filter = props.filter;
  const column = columnByKey(filter.column);
  const kind = column?.kind ?? 'num';
  const categories = column === undefined ? [] : categoryOptionsFor(column);

  return (
    <div
      data-testid="mw-filter-row"
      data-filter={filter.id}
      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
    >
      <button
        type="button"
        data-testid="mw-filter-arm"
        aria-pressed={filter.on}
        aria-label={'Apply filter: ' + predicateText(filter)}
        onClick={() => props.onChange({ on: !filter.on })}
        style={{ ...CHIP, width: 24, padding: 0, color: filter.on ? 'var(--primary)' : undefined }}
      >
        {filter.on ? '✓' : '·'}
      </button>
      <select
        data-testid="mw-filter-column"
        value={filter.column}
        onChange={(event) => props.onChange({ column: event.target.value })}
        style={{ ...INPUT, width: 132 }}
      >
        {FILTERABLE_COLUMNS.map((option: RecognitionColumn) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      <select
        data-testid="mw-filter-op"
        value={filter.op}
        onChange={(event) => props.onChange({ op: event.target.value as FilterPredicate['op'] })}
        style={{ ...INPUT, width: 92 }}
      >
        {OPS_FOR[kind].map((op) => (
          <option key={op} value={op}>
            {op}
          </option>
        ))}
      </select>
      {kind === 'cat' ? (
        <select
          data-testid="mw-filter-value"
          value={filter.a}
          onChange={(event) => props.onChange({ a: event.target.value })}
          style={{ ...INPUT, width: 156 }}
        >
          <option value="">—</option>
          {categories.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          data-testid="mw-filter-value"
          value={filter.a}
          onChange={(event) => props.onChange({ a: event.target.value })}
          placeholder={kind === 'num' ? 'VALUE' : 'TEXT'}
          style={{ ...INPUT, width: 110 }}
        />
      )}
      {filter.op === 'BETWEEN' && (
        <input
          type="text"
          data-testid="mw-filter-value-b"
          value={filter.b}
          onChange={(event) => props.onChange({ b: event.target.value })}
          placeholder="VALUE"
          style={{ ...INPUT, width: 110 }}
        />
      )}
      {column?.unit != null && (
        <span style={{ ...DATA_TAG, color: 'var(--text-muted)' }}>{column.unit}</span>
      )}
      <span style={{ flex: 1 }} />
      <button
        type="button"
        data-testid="mw-filter-remove"
        // The visible child is a glyph, so the name has to be written. It
        // names the predicate it removes rather than saying "remove": there
        // are as many of these as the operator has added.
        aria-label={'Remove filter: ' + predicateText(props.filter)}
        onClick={props.onRemove}
        style={CHIP}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * The frozen row-label column.
 *
 * TYPE stays put while the reveal scrolls past it, which is the one thing the
 * old tier system was right about: a table with no row labels is not a narrower
 * table, it is a different document. Each user sets its own opaque background,
 * because a transparent sticky cell shows the columns sliding underneath it.
 */
const FROZEN = {
  position: 'sticky',
  left: 0,
  zIndex: 'var(--z-panel-sticky)',
  borderRight: '1px solid var(--rule)',
} as const;

const CELL = {
  ...DATA,
  flex: 'none',
  padding: '0 10px',
  fontWeight: 400,
  overflow: 'hidden',
} as const;

const HEAD = {
  ...DATA_LABEL,
  fontWeight: 700,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
} as const;

const INPUT = {
  ...DATA,
  flex: 'none',
  height: 24,
  padding: '0 6px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  fontWeight: 400,
} as const;
